// Pontos de embarque/desembarque das reservas para o mapa da carona.
//
// Por que existe: uma reserva pode terminar numa cidade que NÃO é parada
// formal da carona (modo turbo/contínuo: o passageiro pinala um ponto em
// qualquer lugar do traçado — ex.: Medianeira numa carona Foz → Campo Mourão
// cuja única parada é Cascavel). O mapa da carona plotava só
// origem/destino/paradas e o destino da reserva ficava invisível.
//
// A coordenada não vem da reserva (guarda só nome + km/índice): ela é
// resolvida SOBRE a geometria da rota — `boardKm`/`alightKm` no modo contínuo
// (interpolação com `pointAtKmAlong`) ou `boardStopIndex`/`alightStopIndex`
// (ponto de `getRoutePoints`) no modo por paradas.
//
// Pontos que já têm pin próprio (origem, destino e paradas da carona) são
// filtrados por raio — senão toda reserva Foz → Cascavel desenharia um segundo
// pin em cima do pin de Cascavel. O mesmo vale entre reservas: dois passageiros
// descendo no mesmo ponto da estrada viram UM pin.
//
// Tudo aqui é puro e determinístico: mesma entrada, mesma saída, sem I/O.

import { type LatLng, haversineKm } from "@/lib/route-matching"
import { pointAtKmAlong } from "@/lib/city-stop-point"

/** Reserva reduzida ao que o mapa precisa (campos já presentes no documento). */
export interface ReservationMapInput {
  boardStopIndex?: number | null
  alightStopIndex?: number | null
  boardKm?: number | null
  alightKm?: number | null
  origin?: string | null
  destination?: string | null
}

export interface ReservationMapPoint {
  lat: number
  lng: number
  /** Nome curto ("Medianeira" de "Medianeira, Paraná, …"). */
  label: string
}

/**
 * Raio de fusão com âncoras já plotadas e entre reservas. 200 m cobre o
 * arredondamento de km (3 casas) e a diferença entre o ponto projetado na
 * rodovia e o vértice da polyline, sem fundir cidades vizinhas.
 */
export const RESERVATION_POINT_MERGE_RADIUS_KM = 0.2

function shortLabel(name?: string | null): string | null {
  const first = (name || "").split(",")[0].trim()
  return first.length > 0 ? first : null
}

function isValidPoint(p: { lat?: number | null; lng?: number | null } | undefined): p is LatLng {
  return !!p && typeof p.lat === "number" && Number.isFinite(p.lat) && typeof p.lng === "number" && Number.isFinite(p.lng)
}

/** Ponto do extremo (embarque/desembarque) de uma reserva sobre a rota. */
function endpointPoint(
  r: ReservationMapInput,
  side: "board" | "alight",
  routePoints: Array<{ lat?: number | null; lng?: number | null }>,
  path: LatLng[]
): LatLng | null {
  const km = side === "board" ? r.boardKm : r.alightKm
  if (typeof km === "number" && Number.isFinite(km)) {
    const at = pointAtKmAlong(path, km)
    return at ? at.point : null
  }
  const index = side === "board" ? r.boardStopIndex : r.alightStopIndex
  if (typeof index === "number" && Number.isInteger(index) && index >= 0 && index < routePoints.length) {
    const p = routePoints[index]
    return isValidPoint(p) ? { lat: p.lat as number, lng: p.lng as number } : null
  }
  return null
}

/**
 * Pontos (embarque + desembarque) das reservas que o mapa da carona deve
 * desenhar, já deduplicados contra `anchors` (origem/destino/paradas) e entre si.
 *
 * `routePoints` é o de `getRoutePoints(ride)`; `path` é o de
 * `buildPathFromRide(ride)` (polyline full-res). Reservas canceladas devem ser
 * filtradas pelo chamador.
 */
export function reservationMapPoints(
  reservations: ReservationMapInput[],
  routePoints: Array<{ lat?: number | null; lng?: number | null }>,
  path: LatLng[],
  anchors: Array<{ lat?: number | null; lng?: number | null }> = []
): ReservationMapPoint[] {
  const out: ReservationMapPoint[] = []

  const tooClose = (p: LatLng, list: Array<{ lat: number; lng: number }>): boolean =>
    list.some((q) => haversineKm(p.lat, p.lng, q.lat, q.lng) <= RESERVATION_POINT_MERGE_RADIUS_KM)

  const anchorPoints = anchors.filter(isValidPoint).map((p) => ({ lat: p.lat as number, lng: p.lng as number }))

  for (const r of reservations) {
    const endpoints: Array<{ side: "board" | "alight"; label: string | null }> = [
      { side: "board", label: shortLabel(r.origin) },
      { side: "alight", label: shortLabel(r.destination) },
    ]
    for (const { side, label } of endpoints) {
      if (!label) continue
      const point = endpointPoint(r, side, routePoints, path)
      if (!point) continue
      if (tooClose(point, anchorPoints)) continue
      if (tooClose(point, out)) continue
      out.push({ lat: point.lat, lng: point.lng, label })
    }
  }

  return out
}
